import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Hook pour envoyer un heartbeat de présence pour l'utilisateur connecté.
 * Met à jour la colonne `last_seen_at` dans la table `profiles`.
 * À appeler dans un composant racine (App.tsx ou MainRoutes).
 */
export function usePresenceHeartbeat() {
  const { user } = useAuth();

  const updatePresence = useCallback(async () => {
    if (!user) return;

    try {
      // Essayer d'abord via RPC
      const { error: rpcError } = await (supabase.rpc as any)('update_user_presence');
      
      if (rpcError) {
        // Fallback: mise à jour directe
        await supabase
          .from('profiles')
          .update({ last_seen_at: new Date().toISOString() } as any)
          .eq('id', user.id);
      }
    } catch (error) {
      // Silencieux - ne pas bloquer l'app si la mise à jour échoue
      console.debug('[Presence] Update failed:', error);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    // Mise à jour immédiate à la connexion
    updatePresence();

    // Heartbeat toutes les 60 secondes
    const interval = setInterval(updatePresence, 60000);

    // Mise à jour lors du changement de visibilité (retour sur l'onglet)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updatePresence();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Nettoyage
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, updatePresence]);
}
